<template>
  <div class="google-sign-in">
    <button class="gsi-material-button" @click="onGoogleButtonClick">
      <div class="gsi-material-button-state" />
      <div class="gsi-material-button-content-wrapper">
        <div class="gsi-material-button-icon">
          <svg
            version="1.1"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 48 48"
            xmlns:xlink="http://www.w3.org/1999/xlink"
            style="display: block"
          >
            <path
              fill="#EA4335"
              d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
            />
            <path
              fill="#4285F4"
              d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
            />
            <path
              fill="#FBBC05"
              d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
            />
            <path
              fill="#34A853"
              d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
            />
            <path fill="none" d="M0 0h48v48H0z" />
          </svg>
        </div>
        <span class="gsi-material-button-contents">{{ (isAdminLocalization ? adminText('adminAll.google_sign_in_f83d46', 'Google Sign in') : $t('googleLogin')) }}</span>
      </div>
    </button>
  </div>
</template>

<script>
import AdminAllMessages from '@/components/admin/AdminAllMessages'
import AdminContext from '@/admin-i18n/context'
import { getIOSVersion } from '@/utils'
import { GoogleAuth } from '@/types'
import { GOOGLE_LOGIN } from '@/graphql/auth.graphql'

export default {
  mixins: [AdminContext, AdminAllMessages],
  name: 'google-login',
  props: {
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      codeClient: null,
      retryTimer: null,
      isPopupMode: false,
    }
  },
  watch: {
    disabled(newValue) {
      if (!newValue) {
        this.initGoogleAuthGIS()
      }
    },
  },
  mounted() {
    this.initGoogleAuthGIS()
  },
  beforeDestroy() {
    if (this.retryTimer) {
      clearTimeout(this.retryTimer)
      this.retryTimer = null
    }
  },
  methods: {
    isEmbedded() {
      try {
        return window.self !== window.top
      } catch (e) {
        return true
      }
    },
    redirectToDefaultBrowser() {
      const currUrl = window.location.href
      if (/iPhone|iPad|iPod/i.test(navigator.userAgent)) {
        const iosVersion = getIOSVersion(navigator.userAgent)
        if (!iosVersion) {
          return
        }
        window.location.href = `${
          iosVersion < 17 ? 'com-apple-mobilesafari-tab:' : 'x-safari-'
        }${currUrl}`
      } else if (/Android/i.test(navigator.userAgent)) {
        window.location.href = `intent://${currUrl.replace(/^https?:\/\//, '')}#Intent;scheme=http${
          currUrl.includes('https://') ? 's' : ''
        };action=android.intent.action.VIEW;S.browser_fallback_url=${encodeURIComponent(
          currUrl,
        )};end`
      }
    },
    initGoogleAuthGIS() {
      if (!window.google || !window.google.accounts || !window.google.accounts.oauth2) {
        if (this.retryTimer) {
          clearTimeout(this.retryTimer)
        }
        this.retryTimer = setTimeout(() => this.initGoogleAuthGIS(), 150)

        return
      }
      if (this.retryTimer) {
        clearTimeout(this.retryTimer)
        this.retryTimer = null
      }
      const forcedUxMode = this.getForcedUxMode()
      this.isPopupMode = forcedUxMode ? forcedUxMode === 'popup' : this.isEmbedded()
      const redirectUri = this.isPopupMode
        ? 'postmessage'
        : GoogleAuth.REDIRECT_URI || `${window.location.origin}/auth/google/callback`
      this.codeClient = window.google.accounts.oauth2.initCodeClient({
        client_id: GoogleAuth.CLIENT_ID,
        scope: GoogleAuth.SCOPE,
        ux_mode: this.isPopupMode ? 'popup' : 'redirect',
        redirect_uri: redirectUri,
        callback: this.handlePopupResponse,
      })
    },
    async handlePopupResponse(response) {
      if (!this.isPopupMode) {
        return
      }
      if (!response) {
        this.$emit('error', this.adminText('adminAll.google_login_failed_please_try_again_97817d', 'Google login failed. Please try again.'))

        return
      }
      if (response.error) {
        this.$emit('error', response.error)

        return
      }
      if (!response.code) {
        this.$emit('error', this.adminText('adminAll.missing_authorization_code_please_try_again_0bc878', 'Missing authorization code. Please try again.'))

        return
      }
      try {
        const { errors } = await this.$apollo.mutate({
          mutation: GOOGLE_LOGIN,
          variables: { authorizationCode: response.code, usePostmessage: true },
        })
        if (errors && errors.length) {
          throw new Error(errors[0].message)
        }
        await this.$store.dispatch('auth/fetchUser')
        this.$emit('success')
      } catch (e) {
        this.$emit('error', e.message || this.adminText('adminAll.unable_to_complete_google_sign_in_4910df', 'Unable to complete Google sign-in.'))
      }
    },
    onGoogleButtonClick(event) {
      if (!this.codeClient) {
        event.preventDefault()
        this.initGoogleAuthGIS()

        return
      }
      const fullPath = `${window.location.pathname}${window.location.search}${window.location.hash}`
      const state = fullPath || '/'
      try {
        sessionStorage.setItem('coreevent_google_redirect_path', state)
      } catch (e) {
        console.warn('Unable to persist Google redirect path', e)
      }
      this.codeClient.requestCode({
        state,
      })
    },
    getForcedUxMode() {
      try {
        const params = new URLSearchParams(window.location.search)
        const value = params.get('google_ux')
        if (value === 'popup' || value === 'redirect') {
          return value
        }
      } catch (e) {
        return null
      }

      return null
    },
  },
}
</script>
<style scoped>
.google-sign-in {
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 32px;
}

.gsi-material-button {
  -moz-user-select: none;
  -webkit-user-select: none;
  -ms-user-select: none;
  -webkit-appearance: none;
  background-color: WHITE;
  background-image: none;
  border: 1px solid #747775;
  -webkit-border-radius: 20px;
  border-radius: 20px;
  -webkit-box-sizing: border-box;
  box-sizing: border-box;
  color: #1f1f1f;
  cursor: pointer;
  font-family: 'Roboto', arial, sans-serif;
  font-size: 14px;
  height: 40px;
  letter-spacing: 0.25px;
  outline: none;
  overflow: hidden;
  padding: 0 12px;
  position: relative;
  text-align: center;
  -webkit-transition: background-color 0.218s, border-color 0.218s, box-shadow 0.218s;
  transition: background-color 0.218s, border-color 0.218s, box-shadow 0.218s;
  vertical-align: middle;
  white-space: nowrap;
  width: auto;
  max-width: 400px;
  min-width: min-content;
}

.gsi-material-button .gsi-material-button-icon {
  height: 20px;
  margin-right: 12px;
  min-width: 20px;
  width: 20px;
}

.gsi-material-button .gsi-material-button-content-wrapper {
  -webkit-align-items: center;
  align-items: center;
  display: flex;
  -webkit-flex-direction: row;
  flex-direction: row;
  -webkit-flex-wrap: nowrap;
  flex-wrap: nowrap;
  height: 100%;
  justify-content: space-between;
  position: relative;
  width: 100%;
}

.gsi-material-button .gsi-material-button-contents {
  -webkit-flex-grow: 1;
  flex-grow: 1;
  font-family: 'Roboto', arial, sans-serif;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  vertical-align: top;
}

.gsi-material-button .gsi-material-button-state {
  -webkit-transition: opacity 0.218s;
  transition: opacity 0.218s;
  bottom: 0;
  left: 0;
  opacity: 0;
  position: absolute;
  right: 0;
  top: 0;
}

.gsi-material-button:disabled {
  cursor: default;
  background-color: #ffffff61;
  border-color: #1f1f1f1f;
}

.gsi-material-button:disabled .gsi-material-button-contents {
  opacity: 38%;
}

.gsi-material-button:disabled .gsi-material-button-icon {
  opacity: 38%;
}

.gsi-material-button:not(:disabled):active .gsi-material-button-state,
.gsi-material-button:not(:disabled):focus .gsi-material-button-state {
  background-color: #303030;
  opacity: 12%;
}

.gsi-material-button:not(:disabled):hover {
  -webkit-box-shadow: 0 1px 2px 0 rgba(60, 64, 67, 0.3), 0 1px 3px 1px rgba(60, 64, 67, 0.15);
  box-shadow: 0 1px 2px 0 rgba(60, 64, 67, 0.3), 0 1px 3px 1px rgba(60, 64, 67, 0.15);
}

.gsi-material-button:not(:disabled):hover .gsi-material-button-state {
  background-color: #303030;
  opacity: 8%;
}

</style>
